/**
 * profileNav.js — sidebar tree for the /perfil shell (PR-B.5).
 *
 * Mirrors the shape of `lib/adminNav.js` but flat (one section). Each item
 * carries the route path, label, icon, and the `allow` array of roles that
 * may see it. `<ProfileLayout/>` filters items via `buildProfileNav(role)` so
 * the sidebar never advertises a route the user can't reach.
 *
 * PR-B.5 keeps every account-holder visible everywhere — Plan + Facturación
 * are placeholders until PR-C wires real billing. Tightening visibility is a
 * config change here, not a layout edit.
 *
 * Path order also drives `firstAllowedProfilePath()` (used by the index route
 * redirect when the user lands on `/perfil` exactly).
 */
import { ROLES } from "./roles.js";

const { MASTER, OWNER, MEMBER } = ROLES;
const ALL_ROLES = [MASTER, OWNER, MEMBER];

export const PROFILE_NAV = [
  {
    path: "/perfil/cuenta",
    sectionId: "cuenta",
    label: "Mi perfil",
    icon: "person",
    description: "Información personal",
    allow: ALL_ROLES,
  },
  {
    path: "/perfil/seguridad",
    sectionId: "seguridad",
    label: "Seguridad",
    icon: "lock",
    description: "Contraseña y acceso",
    allow: ALL_ROLES,
  },
  {
    path: "/perfil/preferencias",
    sectionId: "preferencias",
    label: "Preferencias",
    icon: "tune",
    description: "Trazas y agrupamiento del mapa",
    allow: ALL_ROLES,
  },
  {
    path: "/perfil/plan",
    sectionId: "plan",
    label: "Plan",
    icon: "card_membership",
    description: "Tu plan actual",
    allow: ALL_ROLES,
  },
  {
    path: "/perfil/facturacion",
    sectionId: "facturacion",
    label: "Facturación",
    icon: "receipt_long",
    description: "Historial de pagos",
    allow: ALL_ROLES,
  },
];

/**
 * Filter PROFILE_NAV down to the items the given role can see. Returns a
 * fresh array. `null`/`undefined` returns [].
 *
 * @param {string|null|undefined} role
 * @returns {Array<typeof PROFILE_NAV[number]>}
 */
export function buildProfileNav(role) {
  if (!role) return [];
  return PROFILE_NAV.filter((item) => item.allow.includes(role));
}

/**
 * First path the given role is allowed to see. Used by the `/perfil` index
 * route to redirect to the default sub-route. Falls back to `/` when the
 * role has no profile access at all (defense-in-depth — every authenticated
 * user has at least the Account tab today).
 */
export function firstAllowedProfilePath(role) {
  const allowed = buildProfileNav(role);
  return allowed[0]?.path ?? "/";
}
